import React, { useState } from 'react';
import Modal from './ui/Modal.jsx';

const CATEGORIES = [
    {
        id: 'bug',
        label: 'Bug',
        icon: (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
        ),
        activeClass: 'border-rose-500/60 bg-rose-500/12 text-rose-400 dark:text-rose-300 ring-rose-500/20',
        activeClassLight: 'border-rose-300 bg-rose-50 text-rose-600 ring-rose-200',
    },
    {
        id: 'idea',
        label: 'Idea',
        icon: (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
        ),
        activeClass: 'border-amber-500/60 bg-amber-500/12 text-amber-400 dark:text-amber-300 ring-amber-500/20',
        activeClassLight: 'border-amber-300 bg-amber-50 text-amber-600 ring-amber-200',
    },
    {
        id: 'praise',
        label: 'Praise',
        icon: (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
        ),
        activeClass: 'border-amber-500/60 bg-amber-500/12 text-amber-400 dark:text-amber-300 ring-amber-500/20',
        activeClassLight: 'border-amber-300 bg-amber-50 text-amber-600 ring-amber-200',
    },
    {
        id: 'question',
        label: 'Question',
        icon: (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        activeClass: 'border-sky-500/60 bg-sky-500/12 text-sky-400 dark:text-sky-300 ring-sky-500/20',
        activeClassLight: 'border-sky-300 bg-sky-50 text-sky-600 ring-sky-200',
    },
];

const FeedbackModal = ({ onClose, userEmail = '' }) => {
    const [category, setCategory] = useState('idea');
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState(userEmail);
    const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;
        setStatus('sending');
        try {
            const res = await fetch('https://formspree.io/f/mvgqzvvb', {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: category, message: message.trim(), email: email.trim() || undefined }),
            });
            if (res.ok) {
                setStatus('sent');
                setTimeout(onClose, 2000);
            } else {
                setStatus('error');
            }
        } catch {
            setStatus('error');
        }
    };

    if (status === 'sent') {
        return (
            <Modal title="" onClose={onClose} size="sm">
                <div className="flex flex-col items-center py-6 gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                        <svg className="h-6 w-6 text-emerald-500 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <p className="text-base font-semibold text-slate-900 dark:text-white">Thanks for the feedback!</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center">We read everything and it helps more than you know.</p>
                </div>
            </Modal>
        );
    }

    return (
        <Modal title="Send Feedback" onClose={onClose} size="md">
            <form className="mt-2 flex flex-col gap-4" onSubmit={handleSubmit}>

                {/* Category chips */}
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">What kind of feedback?</p>
                    <div className="grid grid-cols-4 gap-2">
                        {CATEGORIES.map(cat => {
                            const isActive = category === cat.id;
                            return (
                                <button
                                    key={cat.id}
                                    type="button"
                                    onClick={() => setCategory(cat.id)}
                                    className={`flex flex-col items-center gap-1.5 rounded-xl py-2.5 px-1 border ring-1 transition-all ${
                                        isActive
                                            ? `${cat.activeClass} ring-1`
                                            : 'border-slate-200 bg-slate-50 text-slate-500 ring-transparent hover:bg-slate-100 hover:text-slate-700 hover:border-slate-300 dark:border-white/8 dark:bg-white/3 dark:text-slate-500 dark:hover:bg-white/6 dark:hover:text-slate-300 dark:hover:border-white/15'
                                    }`}
                                >
                                    {cat.icon}
                                    <span className="text-[11px] font-medium leading-none">{cat.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Message */}
                <div>
                    <label htmlFor="fb-message" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block mb-2">
                        Message <span className="text-rose-500 dark:text-rose-400">*</span>
                    </label>
                    <textarea
                        id="fb-message"
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        required
                        rows={3}
                        placeholder={
                            category === 'bug' ? 'What happened? What did you expect?' :
                            category === 'idea' ? 'What would make FlashFonic better for you?' :
                            category === 'praise' ? 'Tell us what you love!' :
                            'What can we help with?'
                        }
                        className="w-full rounded-xl bg-white border border-slate-200 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-400 resize-none transition dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-200 dark:placeholder:text-slate-600 dark:focus:border-teal-500/50"
                    />
                </div>

                {/* Email */}
                <div>
                    <label htmlFor="fb-email" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block mb-2">
                        Email <span className="text-slate-400 font-normal normal-case tracking-normal">(optional — if you'd like a reply)</span>
                    </label>
                    <input
                        id="fb-email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full rounded-xl bg-white border border-slate-200 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-400 transition dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-200 dark:placeholder:text-slate-600 dark:focus:border-teal-500/50"
                    />
                </div>

                {status === 'error' && (
                    <p className="text-xs text-rose-500 dark:text-rose-400">Something went wrong — try again or email us directly.</p>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-2 pt-1">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center rounded-xl px-4 py-2 text-sm text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 ring-1 ring-slate-200 dark:text-slate-400 dark:hover:text-white dark:bg-slate-800/60 dark:ring-white/10 dark:hover:bg-slate-800 transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!message.trim() || status === 'sending'}
                        className="inline-flex items-center gap-2 rounded-xl px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg shadow-teal-500/20"
                    >
                        {status === 'sending' ? (
                            <>
                                <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                Sending…
                            </>
                        ) : 'Send'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default FeedbackModal;
